declare let mxCellRenderer;
declare let mxConnector;
declare let mxConstants;
declare let mxGraphView;
declare let mxPoint;
declare let mxUtils;

export const JUMP_STYLE = 'jumpStyle';
export const JUMP_STYLE_ARC = 'arc';

const JUMP_SIZE = 12;
const ARC_BULGE = 1.3;
const JOINT_TOLERANCE = 0.5;

const WAYPOINT = 0;
const CROSSING = 1;

/**
 * Line jumps: where two edges with jumpStyle=arc cross, the edge validated later (later in the model) draws an arc
 * over the earlier one. Curved edges neither jump nor are jumped over. Ported from draw.io's Graph.js (mxGraph 3.7.5+
 * grapheditor) onto mxGraph 3.5, arc style only, fixed size.
 */
export function extendLineJumps() {
  const isJumpCandidate = state =>
    state.view.graph.model.isEdge(state.cell) && state.style[mxConstants.STYLE_CURVED] != 1;

  const resetValidationState = mxGraphView.prototype.resetValidationState;

  mxGraphView.prototype.resetValidationState = function () {
    resetValidationState.call(this);
    this.validEdges = [];
  };

  const validateCellState = mxGraphView.prototype.validateCellState;

  mxGraphView.prototype.validateCellState = function (cell, recurse = true) {
    let state = this.getState(cell);

    // A valid edge is not updated again, so its jumps over the edges validated before it are refreshed here
    if (
      state != null &&
      recurse &&
      !state.invalid &&
      isJumpCandidate(state) &&
      this.updateLineJumps(state)
    ) {
      this.graph.cellRenderer.redraw(state, false, this.isRendering());
    }

    state = validateCellState.call(this, cell, recurse);

    if (state != null && recurse && isJumpCandidate(state)) {
      this.validEdges.push(state);
    }

    return state;
  };

  const updateCellState = mxGraphView.prototype.updateCellState;

  mxGraphView.prototype.updateCellState = function (state) {
    updateCellState.call(this, state);

    if (isJumpCandidate(state)) {
      this.updateLineJumps(state);
    }
  };

  /**
   * Sets state.routedPoints to the edge's points plus, in order along each segment, the points where it crosses the
   * edges validated before it. Returns true if they changed.
   */
  mxGraphView.prototype.updateLineJumps = function (state) {
    const pts = state.absolutePoints;
    const previous = state.routedPoints;
    let changed = previous != null;
    let actual = null;

    if (
      pts != null &&
      this.validEdges != null &&
      mxUtils.getValue(state.style, JUMP_STYLE) === JUMP_STYLE_ARC
    ) {
      const tolerance = JOINT_TOLERANCE * this.scale;
      const straightDistSq = this.scale * this.scale;

      changed = false;
      actual = [];

      const addPoint = (type, x, y) => {
        const point = new mxPoint(x, y);

        point.type = type;
        actual.push(point);
        const curr = previous?.[actual.length - 1];

        return curr == null || curr.type != type || curr.x != x || curr.y != y;
      };

      let start = 0;

      while (start < pts.length - 1) {
        const end = straightRunEnd(pts, start, straightDistSq);
        const p0 = pts[start];
        const p1 = pts[end];
        const crossings = [];

        changed = addPoint(WAYPOINT, p0.x, p0.y) || changed;

        for (const other of this.validEdges) {
          const pts2 = other.absolutePoints;

          if (pts2 == null || !mxUtils.intersects(state, other)) {
            continue;
          }

          let start2 = 0;

          while (start2 < pts2.length - 1) {
            const end2 = straightRunEnd(pts2, start2, straightDistSq);
            const p2 = pts2[start2];
            const p3 = pts2[end2];
            const pt = mxUtils.intersection(p0.x, p0.y, p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);

            // A crossing at a segment end (a shared port, a joint) is not a jump
            if (pt != null && [p0, p1, p2, p3].every(p => isAway(pt, p, tolerance))) {
              addCrossing(crossings, pt, p0, tolerance);
            }

            start2 = end2;
          }
        }

        for (const crossing of crossings) {
          changed = addPoint(CROSSING, crossing.x, crossing.y) || changed;
        }

        start = end;
      }

      const end = pts[pts.length - 1];

      changed = addPoint(WAYPOINT, end.x, end.y) || changed;
    }

    state.routedPoints = actual;

    return changed;
  };

  const paintLine = mxConnector.prototype.paintLine;

  mxConnector.prototype.paintLine = function (c, absPts, rounded) {
    const routed = this.state?.routedPoints;

    // Kept on the shape so that redrawShape can tell when only the jumps changed
    this.routedPoints = routed;

    if (this.outline || this.style == null || !routed?.length) {
      paintLine.call(this, c, absPts, rounded);

      return;
    }

    const arcSize =
      mxUtils.getValue(this.style, mxConstants.STYLE_ARCSIZE, mxConstants.LINE_ARCSIZE) / 2;
    const halfWidth = JUMP_SIZE / 2 - 1 + this.strokewidth;
    const halfWidthSq = halfWidth * halfWidth;
    let pts = [];
    let last = null;
    let len = null;
    let n = null;

    c.begin();

    for (let i = 0; i < routed.length; i++) {
      const rpt = routed[i];
      let pt = new mxPoint(rpt.x / this.scale, rpt.y / this.scale);
      let done = false;

      // The ends come from absPts, which are already shortened for the markers
      if (i == 0) {
        pt = absPts[0];
      } else if (i == routed.length - 1) {
        pt = absPts[absPts.length - 1];
      }

      if (last != null && rpt.type == CROSSING) {
        // After the last crossing comes the painted end, which stops short of the end marker
        const next =
          i + 1 == routed.length - 1
            ? absPts[absPts.length - 1]
            : new mxPoint(routed[i + 1].x / this.scale, routed[i + 1].y / this.scale);
        const nextDistSq = (next.x - pt.x) ** 2 + (next.y - pt.y) ** 2;

        if (n == null) {
          n = new mxPoint(pt.x - last.x, pt.y - last.y);
          len = Math.sqrt(n.x * n.x + n.y * n.y);

          if (len > 0) {
            n.x = (n.x * halfWidth) / len;
            n.y = (n.y * halfWidth) / len;
          } else {
            n = null;
          }
        }

        const lastDistSq = (last.x - pt.x) ** 2 + (last.y - pt.y) ** 2;
        const nextIsAhead = len > 0 && (next.x - pt.x) * n.x + (next.y - pt.y) * n.y > 0;

        // A jump that does not fit between its neighbours, or lies past the painted end, is skipped
        if (nextIsAhead && nextDistSq > halfWidthSq && lastDistSq > halfWidthSq) {
          const p0 = new mxPoint(pt.x - n.x, pt.y - n.y);
          const p1 = new mxPoint(pt.x + n.x, pt.y + n.y);
          const side =
            Math.round(n.x) < 0 || (Math.round(n.x) == 0 && Math.round(n.y) <= 0) ? 1 : -1;
          const f = side * ARC_BULGE;

          pts.push(p0);
          this.addPoints(c, pts, rounded, arcSize, false);
          c.curveTo(p0.x - n.y * f, p0.y + n.x * f, p1.x - n.y * f, p1.y + n.x * f, p1.x, p1.y);
          pts = [p1];
          // The next jump has to fit after this arc's end, not after the point before it
          last = p1;
          done = true;
        }
      } else {
        n = null;
      }

      if (!done) {
        last = pt;

        // A skipped crossing is not a corner: the line runs straight through it, and never past its painted end
        if (rpt.type != CROSSING) {
          pts.push(pt);
        }
      }
    }

    this.addPoints(c, pts, rounded, arcSize, false);
    c.stroke();
  };

  const redrawShape = mxCellRenderer.prototype.redrawShape;

  // mxGraph 3.5 has no isShapeInvalid hook: an edge whose jumps changed while its points did not is repainted here
  mxCellRenderer.prototype.redrawShape = function (state, force, rendering) {
    const shapeJumps = state.shape?.routedPoints;
    const jumpsChanged =
      shapeJumps != null &&
      state.routedPoints != null &&
      shapeJumps !== state.routedPoints &&
      !mxUtils.equalPoints(shapeJumps, state.routedPoints);

    return redrawShape.call(this, state, force || jumpsChanged, rendering);
  };
}

/** Index of the point that ends the segment starting at pts[start]: waypoints on a straight line do not split it. */
function straightRunEnd(pts, start, straightDistSq) {
  const p0 = pts[start];
  let end = start + 1;

  while (
    end < pts.length - 1 &&
    mxUtils.ptSegDistSq(p0.x, p0.y, pts[end + 1].x, pts[end + 1].y, pts[end].x, pts[end].y) <
      straightDistSq
  ) {
    end++;
  }

  return end;
}

function isAway(pt, p, tolerance) {
  return Math.abs(pt.x - p.x) > tolerance || Math.abs(pt.y - p.y) > tolerance;
}

/** Inserts the crossing ordered by its distance from the segment start; crossings within tolerance count once. */
function addCrossing(crossings, pt, start, tolerance) {
  const crossing = {distSq: (pt.x - start.x) ** 2 + (pt.y - start.y) ** 2, x: pt.x, y: pt.y};

  if (crossings.every(item => isAway(item, crossing, tolerance))) {
    const after = crossings.findIndex(item => item.distSq > crossing.distSq);

    crossings.splice(after >= 0 ? after : crossings.length, 0, crossing);
  }
}
